import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { useCapabilities } from "@/hooks/use-capabilities";
import { runServerFn } from "@/lib/server-fn";
import {
  listWavePolicies,
  upsertWavePolicy,
  deleteWavePolicy,
} from "@/lib/wave-policies.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/wave-policies")({
  component: WavePoliciesAdmin,
});

const PRIORITY_LABEL: Record<string, string> = {
  carrier_cutoff: "Carrier cutoff",
  committed_arrival: "Committed arrival",
  requested_ship_date: "Requested ship date",
  priority: "Order priority",
  created_at: "Created at",
};
const PRIORITY_KEYS = [
  "carrier_cutoff",
  "committed_arrival",
  "requested_ship_date",
  "priority",
  "created_at",
] as const;

type PolicyRow = {
  id: string;
  tenant_id: string;
  facility_id: string | null;
  name: string;
  description: string | null;
  filter_rules: Record<string, any>;
  priority_keys: string[];
  release_gates: Record<string, any>;
  window_rules: Record<string, any>;
  is_default: boolean;
  is_active: boolean;
};

function WavePoliciesAdmin() {
  const { tenantId, facilities } = useScope();
  const caps = useCapabilities();
  const list = useServerFn(listWavePolicies);
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PolicyRow | null>(null);
  const [creating, setCreating] = useState(false);

  const q = useQuery({
    queryKey: ["wave-policies", tenantId],
    enabled: !!tenantId,
    queryFn: () => list({ data: { tenantId: tenantId! } }),
  });

  const policies = (q.data?.policies ?? []) as PolicyRow[];
  const enabled = caps.isEnabled("wave_planning");

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-5xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Wave policies</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Policies decide which orders group into a wave, how they're prioritized, and which
            gates must pass before release. Manual release is the v1 default.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} disabled={!tenantId}>
          <Plus className="h-4 w-4" /> New policy
        </Button>
      </div>

      {!enabled && (
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="pt-4 flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-warning mt-0.5" />
            <div className="text-sm">
              <div className="font-medium">Wave Planning is not enabled for this tenant.</div>
              <div className="text-muted-foreground mt-0.5">
                Policies can be configured here, but the operator surface in{" "}
                <Link to="/app/pick" className="underline">Pick Release</Link>{" "}
                won't show wave management until the capability is enabled.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Policies</CardTitle>
          <CardDescription>One or more per tenant. Optional facility scope.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Facility</TableHead>
                <TableHead>Priority keys</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[120px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.isLoading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : policies.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No policies yet. Create one to get started.</TableCell></TableRow>
              ) : policies.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <div className="font-medium">{p.name}</div>
                    {p.description && <div className="text-xs text-muted-foreground line-clamp-1">{p.description}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.facility_id ? facilities.find((f) => f.id === p.facility_id)?.name ?? "—" : "All facilities"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {(p.priority_keys ?? []).map((k) => PRIORITY_LABEL[k] ?? k).join(" → ")}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1.5">
                      <Badge variant={p.is_active ? "default" : "secondary"}>
                        {p.is_active ? "Active" : "Inactive"}
                      </Badge>
                      {p.is_default && <Badge variant="outline">Default</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        if (!confirm(`Delete policy "${p.name}"?`)) return;
                        await runServerFn(
                          () => deleteWavePolicy({ data: { tenantId: tenantId!, policyId: p.id } }),
                          "Deleting policy",
                        );
                        qc.invalidateQueries({ queryKey: ["wave-policies", tenantId] });
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {(creating || editing) && (
        <PolicyEditor
          tenantId={tenantId!}
          facilities={facilities}
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["wave-policies", tenantId] });
            setEditing(null);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function PolicyEditor({
  tenantId, facilities, initial, onClose, onSaved,
}: {
  tenantId: string;
  facilities: { id: string; name: string }[];
  initial: PolicyRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const upsert = useServerFn(upsertWavePolicy);
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [facilityId, setFacilityId] = useState<string | null>(initial?.facility_id ?? null);
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [isDefault, setIsDefault] = useState(initial?.is_default ?? false);
  const [priorityKeys, setPriorityKeys] = useState<string[]>(
    initial?.priority_keys ?? ["carrier_cutoff", "committed_arrival", "created_at"],
  );
  const [filterText, setFilterText] = useState(
    JSON.stringify(initial?.filter_rules ?? {}, null, 2),
  );
  const [gatesText, setGatesText] = useState(
    JSON.stringify(initial?.release_gates ?? { skip_status_check: false, block_on_hold: true }, null, 2),
  );
  const [windowText, setWindowText] = useState(
    JSON.stringify(initial?.window_rules ?? {}, null, 2),
  );
  const [saving, setSaving] = useState(false);

  const moveKey = (idx: number, dir: -1 | 1) => {
    const next = [...priorityKeys];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setPriorityKeys(next);
  };
  const toggleKey = (k: string) => {
    setPriorityKeys((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  };

  const save = async () => {
    let filter, gates, win;
    try { filter = JSON.parse(filterText || "{}"); }
    catch { toast.error("Filter rules: invalid JSON"); return; }
    try { gates = JSON.parse(gatesText || "{}"); }
    catch { toast.error("Release gates: invalid JSON"); return; }
    try { win = JSON.parse(windowText || "{}"); }
    catch { toast.error("Window rules: invalid JSON"); return; }
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (priorityKeys.length === 0) { toast.error("At least one priority key is required"); return; }
    setSaving(true);
    try {
      await runServerFn(
        () => upsert({
          data: {
            id: initial?.id,
            tenantId,
            facilityId,
            name: name.trim(),
            description: description?.trim() || null,
            filterRules: filter,
            priorityKeys: priorityKeys as any,
            releaseGates: gates,
            windowRules: win,
            isActive,
            isDefault,
          },
        }),
        "Saving wave policy",
      );
      toast.success(initial ? "Policy updated" : "Policy created");
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit wave policy" : "New wave policy"}</DialogTitle>
          <DialogDescription>
            Policies are deterministic. Same inputs → identical included / excluded decisions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Facility scope</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                value={facilityId ?? ""}
                onChange={(e) => setFacilityId(e.target.value || null)}
              >
                <option value="">All facilities</option>
                {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description ?? ""} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={isActive} onCheckedChange={setIsActive} /> Active
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={isDefault} onCheckedChange={setIsDefault} /> Default for scope
            </label>
          </div>

          <div>
            <Label>Priority keys (ordered)</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Planner ranks included orders by these keys in order. Tie-broken by order id.
            </p>
            <div className="space-y-1 border rounded-md p-2">
              {priorityKeys.map((k, idx) => (
                <div key={k} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-muted/40">
                  <span className="text-sm">{idx + 1}. {PRIORITY_LABEL[k] ?? k}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => moveKey(idx, -1)} disabled={idx === 0}>
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => moveKey(idx, 1)} disabled={idx === priorityKeys.length - 1}>
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleKey(k)}>Remove</Button>
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap gap-1.5 pt-2">
                {PRIORITY_KEYS.filter((k) => !priorityKeys.includes(k)).map((k) => (
                  <Button key={k} variant="outline" size="sm" onClick={() => toggleKey(k)}>
                    <Plus className="h-3 w-3" /> {PRIORITY_LABEL[k]}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <JsonField
              label="Filter rules (JSON)"
              help='Optional. e.g. {"carrier": ["UPS","FEDEX"], "hold_reason_is_null": true}'
              value={filterText} onChange={setFilterText}
            />
            <JsonField
              label="Release gates (JSON)"
              help='Optional. Built-in keys: block_on_hold, block_on_commitment_breach, min_orders, skip_status_check.'
              value={gatesText} onChange={setGatesText}
            />
            <JsonField
              label="Window rules (JSON)"
              help='Optional. e.g. {"length_minutes": 60} — used by planner if it understands the keys.'
              value={windowText} onChange={setWindowText}
            />
          </div>

          <Card className="border-muted">
            <CardContent className="pt-4 text-xs text-muted-foreground">
              <strong className="text-foreground">Auto-release</strong> is deferred. When it ships
              it will be a per-policy opt-in flag, fully explainable and auditable; manual release
              remains available.
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save policy"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JsonField({
  label, help, value, onChange,
}: { label: string; help: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <Label>{label}</Label>
      <p className="text-xs text-muted-foreground mb-1">{help}</p>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="font-mono text-xs"
      />
    </div>
  );
}